<template>
  <div class="file-manager-wrapper">
    <div class="project-documents-section">
      <!-- Show full page header when used as standalone component, simplified header when embedded -->
      <AppPageHeader v-if="!props.projectId" :title="$t('documents.title')" :subtitle="$t('documents.subtitle')">
        <template #actions>          
          <PermissionGuard :permission="'UPLOAD_DOCUMENT'">
            <label class="upload-btn-wrapper">
              <AppButton variant="primary" size="medium" class="upload-file-btn" @click="handleUploadClick" :disabled="uploading">
                <span class="btn-icon">+</span> {{ $t('documents.uploadFile') }}
              </AppButton>
            </label>
          </PermissionGuard>
        </template>
      </AppPageHeader>

      <!-- Simplified header when embedded in project details -->
      <div v-else class="embedded-header">
        <h2 class="component-title"></h2>
        <PermissionGuard :permission="'UPLOAD_DOCUMENT'">
          <label class="upload-btn-wrapper">
            <button class="btn btn-primary" @click="handleUploadClick">
              {{ $t('documents.uploadFile') }}
            </button>
          </label>
        </PermissionGuard>
      </div>
      <!-- Project context display when coming from a project (but not when already in project details) -->
      <div v-if="sourceProject && !props.projectId" class="project-context">
        <div class="project-context-content">
          <div class="project-info">
            <h3>{{ $t('documents.projectFiles', { projectName: sourceProject.name }) }}</h3>
            <p>{{ $t('documents.projectFilesDescription') }}</p>
          </div>
          <AppButton variant="outline" size="small" @click="returnToProject" class="return-to-project-btn">
            <span class="btn-icon">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path fill="currentColor" d="M20,11V13H8L13.5,18.5L12.08,19.92L4.16,12L12.08,4.08L13.5,5.5L8,11H20Z" />
              </svg>
            </span>
            {{ $t('documents.returnToProject') }}
          </AppButton>
        </div>
      </div>

      <AppCard class="mt-lg">
        <div v-if="loading" class="loading-indicator">
          <div class="loading-content">
            <div class="spinner"></div>
            <p>{{ $t('documents.loading') }}</p>
          </div>
        </div>

        <div v-else-if="error" class="error-message">
          <div class="error-content">
            <i class="fas fa-exclamation-triangle error-icon"></i>
            <p>{{ error }}</p>
            <button class="btn btn-outline" @click="fetchFiles">{{ $t('common.retry') }}</button>
          </div>
        </div>

        <div v-else-if="files.length === 0" class="empty-state">
          <div class="empty-content">
            <div class="empty-icon">
              <i class="fas fa-folder-open"></i>
            </div>
            <h3>{{ $t('documents.noFiles.title') }}</h3>
            <p>{{ $t('documents.noFiles.description') }}</p>
            <PermissionGuard :permission="'UPLOAD_DOCUMENT'">
              <button class="btn btn-primary" @click="handleUploadClick">
                <i class="fas fa-plus"></i>
                {{ $t('documents.uploadFirstFile') }}
              </button>
            </PermissionGuard>
          </div>
        </div>

        <div v-else class="files-container">
          <!-- Document Statistics -->
          <section class="stats-section">
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-icon files">
                  <i class="fas fa-file"></i>
                </div>
              </div>
              <div class="stat-number">{{ files.length }}</div>
              <div class="stat-label">{{ $t('documents.stats.totalFiles', { count: files.length }) }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-icon storage">
                  <i class="fas fa-database"></i>
                </div>
              </div>
              <div class="stat-number">{{ formatTotalSize() }}</div>
              <div class="stat-label">{{ $t('documents.stats.storageUsed') }}</div>
            </div>
            <div class="stat-card">
              <div class="stat-header">
                <div class="stat-icon recent">
                  <i class="fas fa-clock"></i>
                </div>
              </div>
              <div class="stat-number">{{ getRecentFilesCount() }}</div>
              <div class="stat-label">{{ $t('documents.stats.recentFiles', { count: getRecentFilesCount() }) }}</div>
            </div>
          </section>

          <!-- Search and Filter Controls -->
          <section class="controls-section">
            <div class="controls-header">
              <h2 class="controls-title">
                <i class="fas fa-filter"></i>
                {{ $t('documents.filterByType') }}
              </h2>
            </div>
            
            <div class="filter-section">
              <div class="filter-left">
                <span class="filter-label">{{ $t('documents.documentType') }}:</span>
                <div class="filter-chips">
                  <button
                    v-for="docType in uniqueDocTypes"
                    :key="docType.type"
                    class="filter-chip"
                    :class="{ active: selectedDocType === docType.type }"
                    @click="selectedDocType = docType.type"
                  >
                    <span>{{ getFilterChipLabel(docType.type) }}</span>
                    <span class="chip-count">{{ docType.count }}</span>
                  </button>
                </div>
              </div>
              
              <div class="filter-right">
                <span class="filter-label">{{ $t('common.pagination.itemsPerPage') }}:</span>
                <div class="page-size-selector">
                  <select 
                    v-model="pageSize" 
                    class="page-size-dropdown"
                  >
                    <option 
                      v-for="size in pageSizeOptions" 
                      :key="size" 
                      :value="size"
                    >
                      {{ size }}
                    </option>
                  </select>
                </div>
              </div>
            </div>
          </section>

          <!-- Documents Grid -->
          <section class="documents-section">
            <div class="documents-grid">
              <div
                v-for="(file, index) in paginatedFiles"
                :key="file.id"
                class="document-card"
                :style="{ 'animation-delay': `${(index % 6) * 0.1 + 0.1}s` }"
                tabindex="0"
                @click="viewDocument(file)"
              >
                <div class="document-header">
                  <div 
                    class="document-icon"
                    :class="getFileTypeCategory(file.fileName)"
                  >
                    <i :class="getFileIconClass(file.fileName)"></i>
                  </div>
                  <div class="document-info">
                    <h3 class="document-name">{{ file.fileName }}</h3>
                    <span class="document-type-badge">{{ file.docType || 'document' }}</span>
                  </div>
                </div>
                <div class="document-meta">
                  <div class="meta-row">
                    <i class="fas fa-weight meta-icon"></i>
                    <span class="file-size">{{ formatFileSize(file.fileSize) }}</span>
                  </div>
                  <div class="meta-row">
                    <i class="fas fa-calendar meta-icon"></i>
                    <span class="file-date">{{ formatDate(file.lastModified || file.createdAt || file.uploadDate) }}</span>
                  </div>
                  <div class="meta-row" v-if="file.uploadedBy || file.createdBy">
                    <i class="fas fa-user meta-icon"></i>
                    <span>{{ $t('documents.uploadedBy', { user: file.uploadedBy || file.createdBy || 'Admin' }) }}</span>
                  </div>
                </div>
                <div class="document-actions">
                  <button 
                    class="action-button" 
                    :title="$t('documents.actions.view')"
                    @click.stop="viewDocument(file)"
                  >
                    <i class="fas fa-eye"></i>
                  </button>
                  <button 
                    v-if="hasPermissionForProject(PermissionType.UPLOAD_DOCUMENT, projectId).value"
                    class="action-button annotate" 
                    :title="$t('documents.actions.annotate')"
                    @click.stop="annotateDocument(file)"
                  >
                    <i class="fas fa-pencil-alt"></i>
                  </button>
                  <button 
                    class="action-button" 
                    :title="$t('documents.actions.download')"
                    @click.stop="downloadFile(file)"
                  >
                    <i class="fas fa-download"></i>
                  </button>
                  <button 
                    v-if="hasPermissionForProject(PermissionType.DELETE_DOCUMENT, projectId).value"
                    class="action-button delete" 
                    :title="$t('documents.actions.delete')"
                    @click.stop="promptDeleteFile(file)"
                  >
                    <i class="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
            
            <!-- Pagination Controls -->
            <div v-if="shouldShowPagination" class="pagination-section">
              <div class="pagination-controls">
                <button 
                  class="pagination-btn"
                  :class="{ disabled: currentPage === 1 }"
                  :disabled="currentPage === 1"
                  @click="goToFirstPage"
                  :title="$t('common.pagination.first')"
                >
                  <i class="fas fa-angle-double-left"></i>
                </button>
                
                <button 
                  class="pagination-btn"
                  :class="{ disabled: currentPage === 1 }"
                  :disabled="currentPage === 1"
                  @click="goToPreviousPage"
                  :title="$t('common.pagination.previous')"
                >
                  <i class="fas fa-angle-left"></i>
                </button>
                
                <div class="pagination-info">
                  <span class="page-numbers">
                    {{ $t('common.pagination.page') }} {{ currentPage }} {{ $t('common.pagination.of') }} {{ totalPages }}
                  </span>
                  <span class="items-info">
                    {{ $t('common.pagination.showing') }} {{ (currentPage - 1) * pageSize + 1 }}-{{ Math.min(currentPage * pageSize, filteredFiles.length) }} {{ $t('common.pagination.of') }} {{ filteredFiles.length }}
                  </span>
                </div>
                
                <button 
                  class="pagination-btn"
                  :class="{ disabled: currentPage === totalPages }"
                  :disabled="currentPage === totalPages"
                  @click="goToNextPage"
                  :title="$t('common.pagination.next')"
                >
                  <i class="fas fa-angle-right"></i>
                </button>
                
                <button 
                  class="pagination-btn"
                  :class="{ disabled: currentPage === totalPages }"
                  :disabled="currentPage === totalPages"
                  @click="goToLastPage"
                  :title="$t('common.pagination.last')"
                >
                  <i class="fas fa-angle-double-right"></i>
                </button>
              </div>
            </div>
          </section>
        </div>
      </AppCard>
      
      <!-- Document Upload Modal -->
      <DocumentUploadModal
        :show="showUploadModal"
        :project-id="projectId"
        :uploading="uploading"
        :show-client-visibility-toggle="isFullManagement && !isClientUser"
        @close="showUploadModal = false"
        @upload="handleFileSelected"
      />

      <!-- File Upload Progress Modal -->
      <transition name="fade">
        <FileUploadModal
          v-if="uploading"
          :progress="uploadProgress"
        />
      </transition>

      <!-- Delete Confirmation Modal -->      <transition name="fade">
        <DeleteConfirmationModal
          v-if="showDeleteConfirmation"          
          :filename="fileToDelete?.fileName || ''"
          :doc-type="fileToDelete?.docType || ''"
          :description="fileToDelete?.description || ''"
          :loading="loading"
          @confirm="confirmDelete"
          @cancel="cancelDelete"
        />
      </transition>

      <!-- Document Viewer Modal -->
      <transition name="fade">
        <DocumentViewerModal
          v-if="showDocumentViewer && selectedDocument"
          :projectId="props.projectId"
          :document="selectedDocument"
          @close="closeDocumentViewer"
        />
      </transition>

      <!-- Document Annotator Modal -->
      <teleport to="body">
        <DocumentAnnotator
          v-if="showDocumentAnnotator && selectedDocumentForAnnotation && documentAnnotatorUrl"
          :projectId="Number(props.projectId)"
          :documentId="Number(selectedDocumentForAnnotation.id)"
          :documentUrl="documentAnnotatorUrl"
          :fileName="selectedDocumentForAnnotation.fileName || selectedDocumentForAnnotation.originalName || selectedDocumentForAnnotation.name"
          :fileType="getFileType(selectedDocumentForAnnotation)"
          @close="closeDocumentAnnotator"
          @saved="handleAnnotationSaved"
        />
      </teleport>
    </div>
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore } from '@/composables/useStore';
import { useProjectStore, useAuthStore } from '@/stores';
import { useNotifications } from '@/composables/useNotifications';
import { usePermissions } from '@/composables/usePermissions';
import DocumentService from '@/services/documents.service';
import type { UploadedFile } from '@/types';

import AppCard from '../../base/AppCard.vue';
import AppButton from '../../base/AppButton.vue';
import AppPageHeader from '../../base/AppPageHeader.vue';
import AppDataGrid from '../../base/AppDataGrid.vue';
import FileUploadModal from './FileUploadModal.vue';
import DocumentUploadModal from './DocumentUploadModal.vue';
import DeleteConfirmationModal from './DeleteConfirmationModal.vue';
import DocumentAnnotator from './DocumentAnnotator.vue';

// Props
interface Props {
  projectId?: string | number | null;
}

const props = withDefaults(defineProps<Props>(), {
  projectId: null
});

// Emits
interface Emits {
  (e: 'document-selected', document: UploadedFile): void;
  (e: 'return-to-project'): void;
}

const emit = defineEmits<Emits>();

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { notifySuccess, notifyError } = useNotifications();
const { hasPermissionForProject } = usePermissions();
const projectStore = useProjectStore();
const authStore = useAuthStore();

import DocumentViewerModal from './DocumentViewerModal.vue';
import PermissionGuard from '../../common/PermissionGuard.vue';
import { PermissionType } from '@/services/permission.service';

// Detect FULL_MANAGEMENT from the current project in the store
const isFullManagement = computed(() =>
  projectStore.currentProject?.managementType === 'FULL_MANAGEMENT'
)

const isClientUser = computed(() => authStore.user?.type === 'CLIENT')

// Data
const files = ref<any[]>([]);
const showDeleteConfirmation = ref(false);
const fileToDelete = ref<any>(null);
const sourceProject = ref<any>(null);
const gridApi = ref<any>(null);
const gridColumnApi = ref<any>(null);
const error = ref<string | null>(null);
const loading = ref(false);
const uploading = ref(false);
const showDocumentViewer = ref(false);
const selectedDocument = ref<any>(null);
const uploadProgress = ref(0);
const showUploadModal = ref(false);
const isInitialLoad = ref(true);
const selectedDocType = ref('all');
const showDocumentAnnotator = ref(false);
const selectedDocumentForAnnotation = ref<any>(null);
const documentAnnotatorUrl = ref<string | null>(null);

// Pagination state
const currentPage = ref(1);
const pageSize = ref(12); // Number of documents per page for grid layout
const pageSizeOptions = [6, 12, 24, 48]; // Available page size options

// Watch for projectId changes
watch(() => props.projectId, () => {
  // Handle projectId changes
}, { immediate: true });

const uniqueDocTypes = computed(() => {
  const types = new Map();
  types.set('all', files.value.length);
  
  files.value.forEach((file: any) => {
    const count = types.get(file.docType) || 0;
    types.set(file.docType, count + 1);
  });
  
  return Array.from(types.entries()).map(([type, count]) => ({
    type,
    count
  }));
});

const filteredFiles = computed(() => {
  let result = files.value;
  
  // Filter by document type
  if (selectedDocType.value !== 'all') {
    result = result.filter((file: any) => file.docType === selectedDocType.value);
  }
  
  return result;
});

// Paginated files for display
const paginatedFiles = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value;
  const end = start + pageSize.value;
  return filteredFiles.value.slice(start, end);
});

// Pagination computed properties
const totalPages = computed(() => {
  return Math.ceil(filteredFiles.value.length / pageSize.value);
});

const shouldShowPagination = computed(() => {
  return filteredFiles.value.length > pageSize.value;
});

const getFileIcon = (filename: string) => {
  if (!filename) return '';

  const extension = filename.split('.').pop()?.toLowerCase();

  const iconMap: Record<string, string> = {
    'pdf': '<i class="fas fa-file-pdf" style="color: #e74c3c;"></i>',
    'doc': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'docx': '<i class="fas fa-file-word" style="color: #2980b9;"></i>',
    'xls': '<i class="fas fa-file-excel" style="color: #27ae60;"></i>',
    'xlsx': '<i class="fas fa-file-excel" style="color: #27ae60;"></i>',
    'ppt': '<i class="fas fa-file-powerpoint" style="color: #e67e22;"></i>',
    'pptx': '<i class="fas fa-file-powerpoint" style="color: #e67e22;"></i>',
    'jpg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'jpeg': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'png': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'gif': '<i class="fas fa-file-image" style="color: #3498db;"></i>',
    'zip': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
    'rar': '<i class="fas fa-file-archive" style="color: #8e44ad;"></i>',
    'txt': '<i class="fas fa-file-alt" style="color: #7f8c8d;"></i>',
    'mp3': '<i class="fas fa-file-audio" style="color: #f39c12;"></i>',
    'mp4': '<i class="fas fa-file-video" style="color: #d35400;"></i>',
  };

  return extension ? (iconMap[extension] || '<i class="fas fa-file" style="color: #95a5a6;"></i>') : '';
};

// Get file type for annotation
const getFileType = (file: any) => {
  const fileName = file?.fileName || file?.originalName || file?.name || '';
  return fileName.split('.').pop()?.toLowerCase() || '';
};

// Get file type category for styling
const getFileTypeCategory = (fileName: string): string => {
  const extension = fileName?.split('.').pop()?.toLowerCase() || '';
  
  if (extension === 'pdf') return 'pdf';
  if (['doc', 'docx'].includes(extension)) return 'word';
  if (['xls', 'xlsx'].includes(extension)) return 'excel';
  if (['ppt', 'pptx'].includes(extension)) return 'powerpoint';
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
  if (['txt', 'csv', 'xml'].includes(extension)) return 'text';
  if (['zip', 'rar', '7z'].includes(extension)) return 'archive';
  
  return 'default';
};

// Get file icon class for Font Awesome
const getFileIconClass = (fileName: string): string => {
  const category = getFileTypeCategory(fileName);
  
  const iconMap: Record<string, string> = {
    pdf: 'fas fa-file-pdf',
    word: 'fas fa-file-word',
    excel: 'fas fa-file-excel',
    powerpoint: 'fas fa-file-powerpoint',
    image: 'fas fa-file-image',
    text: 'fas fa-file-alt',
    archive: 'fas fa-file-archive',
    default: 'fas fa-file'
  };
  
  return iconMap[category] || iconMap.default;
};

// Format total file size
const formatTotalSize = (): string => {
  const totalBytes = files.value.reduce((sum: number, file: any) => sum + (file.fileSize || 0), 0);
  return formatFileSize(totalBytes);
};

// Get count of recent files (uploaded in last 7 days)
const getRecentFilesCount = (): number => {
  const threeDaysAgo = new Date();
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);
  
  return files.value.filter((file: any) => {
    const fileDate = new Date(file.lastModified || file.createdAt || 0);
    return fileDate > threeDaysAgo;
  }).length;
};

// Format file size helper
const formatFileSize = (bytes: number | undefined): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(1));
  return `${size} ${sizes[i]}`;
};

// Format date helper
const formatDate = (dateString: string | Date | null | undefined): string => {
  if (!dateString) return t('documents.noDate');
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return t('documents.invalidDate');
    return date.toLocaleDateString();
  } catch (error) {
    return t('documents.invalidDate');
  }
};

// Get filter chip label helper
const getFilterChipLabel = (type: string): string => {
  if (type === 'all') return t('common.all');
  
  // Map file extensions to user-friendly labels
  const typeLabels: Record<string, string> = {
    'pdf': 'PDF',
    'doc': 'Word',
    'docx': 'Word',
    'xls': 'Excel',
    'xlsx': 'Excel',
    'ppt': 'PowerPoint',
    'pptx': 'PowerPoint',
    'txt': t('documents.types.text'),
    'jpg': t('documents.types.image'),
    'jpeg': t('documents.types.image'),
    'png': t('documents.types.image'),
    'gif': t('documents.types.image'),
    'zip': t('documents.types.archive'),
    'rar': t('documents.types.archive'),
    '7z': t('documents.types.archive')
  };
  
  return typeLabels[type.toLowerCase()] || type.toUpperCase();
};

// Handle upload button click
const handleUploadClick = () => {
  if (!props.projectId) {
    const errorMsg = t('notifications.selectFirmFirst');
    notifyError(errorMsg);
    error.value = errorMsg;
    return;
  }
    showUploadModal.value = true;
};

// Grid Configuration - make reactive to language changes
const columnDefs = computed(() => [
  {
    headerName: '#',
    field: 'rowNumber',
    valueGetter: (params: any) => params.node.rowIndex + 1,
    width: 70,
    pinned: 'left',
    suppressMenu: true,
    sortable: false,
    filter: false,
    resizable: false,
  },
  {
    field: 'fileType',
    headerName: '',
    width: 60,
    cellRenderer: (params: any) => {
      const span = document.createElement('span');
      span.innerHTML = getFileIcon(params.data.fileName);
      return span;
    },
    resizable: false,
    sortable: false,
    filter: false
  },
  {
    field: 'fileName',
    headerName: t('documents.filename'),
    type: 'text',
    sortable: true,
    resizable: true,
    flex: 1,
    minWidth: 150
  },
  {
    field: 'docType',
    headerName: t('documents.documentType'),
    type: 'text',
    sortable: true,
    resizable: true,
    minWidth: 100
  },
  {
    field: 'createdAt',
    headerName: t('documents.uploadDate'),
    type: 'date',
    sortable: true,
    resizable: true,
    minWidth: 180
  }
]);

// Actions configuration for AppDataGrid - make reactive to language changes
const actions = computed(() => {
  const baseActions = [
    {
      name: 'view',
      icon: 'fas fa-eye',
      label: t('documents.view'),
      tooltip: t('documents.view'),
      class: 'view-btn'
    },
    {
      name: 'download',
      icon: 'fas fa-download',
      label: t('documents.download'),
      tooltip: t('documents.download'),
      class: 'download-btn'
    }
  ];

  // Add annotate action if user has permission and document is annotatable
  if (hasPermissionForProject(PermissionType.UPLOAD_DOCUMENT, props.projectId).value) {
    baseActions.push({
      name: 'annotate',
      icon: 'fas fa-pencil-alt',
      label: t('annotation.annotate'),
      tooltip: t('annotation.annotate'),
      class: 'annotate-btn',
      // Show annotate only for supported file types
      condition: (data: any) => {
        const annotableTypes = ['jpg', 'jpeg', 'png', 'pdf', 'gif', 'webp', 'bmp'];
        const fileName = data?.fileName || data?.originalName || data?.name || '';
        const fileType = fileName.split('.').pop()?.toLowerCase() || '';
        return annotableTypes.includes(fileType);
      }
    } as any);
  }

  // Only add delete action if user has permission
  if (hasPermissionForProject(PermissionType.DELETE_DOCUMENT, props.projectId).value) {
    baseActions.push({
      name: 'delete',
      icon: 'fas fa-trash',
      label: t('documents.delete'),
      tooltip: t('documents.delete'),
      class: 'delete-btn',
      // Show delete only when current user is the creator (createdBy)
      condition: (data: any) => {
        try {
          const user = store.getters.getUser.value;
          if (!user) return false;
          const uid = String(user.id || '');
          return data?.createdBy && String(data.createdBy) === uid;
        } catch (e) {
          return false;
        }
      }
    } as any);
  }

  return baseActions;
});

const defaultColDef = {
  resizable: true,
  wrapText: false,
  autoHeight: false,
};

// Fetch files
const fetchFiles = async (force = false) => {
  if (!props.projectId) {
    const errorMsg = t('notifications.selectProjectFirst');
    notifyError(errorMsg);
    error.value = errorMsg;
    files.value = [];
    return;
  }

  // Prevent double loading unless forced
  if (loading.value || (!force && !isInitialLoad.value)) {
    return;
  }

  loading.value = true;
  error.value = null;
  try {
    const response = await DocumentService.getProjectDocuments(props.projectId);
    files.value = response || [];
    isInitialLoad.value = false;
  } catch (err: any) {
    console.error('Error fetching files:', err);
    
    let errorMessage = t('errors.loadingFilesError');
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    notifyError(errorMessage);
    error.value = errorMessage;
  } finally {
    loading.value = false;
  }
};

// Grid event handlers
const onGridReady = (params: any) => {
  gridApi.value = params.api;
  gridColumnApi.value = params.columnApi;

  // Auto-size all columns to fit the content
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};
// File upload handling
const handleFileSelected = async (formData: FormData) => {
  uploading.value = true;
  uploadProgress.value = 0;

  try {
    // Provide axios upload progress handler to update UI
    await DocumentService.uploadProjectDocuments(props.projectId, formData, (progressEvent: any) => {
      try {
        if (!progressEvent) return
        // AxiosProgressEvent may have 'loaded' and 'total'
        const loaded = (progressEvent.loaded as number) || 0
        const total = (progressEvent.total as number) || 0
        if (total > 0) {
          uploadProgress.value = Math.round((loaded / total) * 100)
        } else {
          // If total is not available, keep at 0 until completed
          uploadProgress.value = 0
        }
      } catch (e) {
        // ignore progress parsing errors
      }
    })
    showUploadModal.value = false;
    notifySuccess(t('documents.uploadSuccess'));
    // Ensure progress reflects completion
    uploadProgress.value = 100
    await fetchFiles(true);
  } catch (err: any) {
    console.error('Error uploading file:', err);
    
    // Extract meaningful error message
    let errorMessage = t('errors.uploadingFileError');
    
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    notifyError(errorMessage);
    
    // Keep the local error for any UI that might need it
    error.value = errorMessage;
  } finally {
    uploading.value = false;
  }
};

// File deletion handling
const downloadFile = async (file: any) => {
  if (!props.projectId || !file?.id) {
    const errorMsg = t('errors.invalidFileDownload');
    notifyError(errorMsg);
    error.value = errorMsg;
    return;
  }

  try {
    loading.value = true;
    await DocumentService.downloadDocument(props.projectId, file.id, file.fileName);
  } catch (err: any) {
    console.error('Error downloading file:', err);
    
    let errorMessage = t('errors.downloadingFileError');
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    notifyError(errorMessage);
    error.value = errorMessage;
  } finally {
    loading.value = false;
  }
};

const promptDeleteFile = (file: any) => {
  if (loading.value) return; // Prevent opening delete modal while loading
  fileToDelete.value = file;
  showDeleteConfirmation.value = true;
};

// View document
const viewDocument = (file: any) => {
  if (loading.value) return; // Prevent opening viewer while loading
  selectedDocument.value = file;
  showDocumentViewer.value = true;
};

// Annotate document
const annotateDocument = async (file: any) => {
  if (loading.value || !props.projectId || !file?.id) return;

  try {
    loading.value = true;
    
    // Get document data for annotation
    const result = await DocumentService.getDocumentForView(props.projectId, file.id);
    
    if (result && result.data instanceof Blob) {
      // Clean up previous URL if exists
      if (documentAnnotatorUrl.value) {
        URL.revokeObjectURL(documentAnnotatorUrl.value);
      }
      
      // Create new object URL for the document
      documentAnnotatorUrl.value = URL.createObjectURL(result.data);
      selectedDocumentForAnnotation.value = file;
      showDocumentAnnotator.value = true;
    } else {
      console.error('Invalid response format - expected a Blob object');
      notifyError(t('errors.invalidResponseFormat'));
    }
  } catch (err: any) {
    console.error('Error loading document for annotation:', err);
    let errorMessage = t('errors.loadingDocumentError');
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    notifyError(errorMessage);
  } finally {
    loading.value = false;
  }
};

// Close document viewer
const closeDocumentViewer = () => {
  showDocumentViewer.value = false;
  selectedDocument.value = null;
};

// Close document annotator
const closeDocumentAnnotator = () => {
  showDocumentAnnotator.value = false;
  selectedDocumentForAnnotation.value = null;
  
  // Clean up the object URL
  if (documentAnnotatorUrl.value) {
    URL.revokeObjectURL(documentAnnotatorUrl.value);
    documentAnnotatorUrl.value = null;
  }
};

// Handle annotation saved
const handleAnnotationSaved = () => {
  notifySuccess(t('annotation.savedSuccessfully'));
  closeDocumentAnnotator();
};

// Pagination methods
const goToFirstPage = () => {
  currentPage.value = 1;
};

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToLastPage = () => {
  currentPage.value = totalPages.value;
};

// Reset to first page when filters or page size changes
watch([selectedDocType, pageSize], () => {
  currentPage.value = 1;
});

const isDeleting = ref(false);

const confirmDelete = async () => {
  if (!fileToDelete.value || !props.projectId || isDeleting.value) return;

  try {
    isDeleting.value = true;
    loading.value = true;
    await DocumentService.deleteProjectDocument(props.projectId, fileToDelete.value.id);
    showDeleteConfirmation.value = false;
    
    // Show success notification
    notifySuccess(t('notifications.fileDeleteSuccess'));
    
    // Clear deleted file reference
    fileToDelete.value = null;
    // Refresh file list after deletion
    await fetchFiles(true);
  } catch (err: any) {
    console.error('Error deleting file:', err);
    
    let errorMessage = t('errors.deletingFileError');
    if (err.response?.data?.message) {
      errorMessage = err.response.data.message;
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    notifyError(errorMessage);
    error.value = errorMessage;
  } finally {
    loading.value = false;
    isDeleting.value = false;
  }
};

// Cancel delete handler
const cancelDelete = () => {
  showDeleteConfirmation.value = false;
  fileToDelete.value = null;
};

// Handle action clicks from AppDataGrid
const handleAction = (action: any, data: any) => {
  if (action.name === 'view') {
    viewDocument(data);
  } else if (action.name === 'download') {
    downloadFile(data);
  } else if (action.name === 'annotate') {
    annotateDocument(data);
  } else if (action.name === 'delete') {
    promptDeleteFile(data);
  }
};

// Window resize handler
const onWindowResize = () => {
  if (gridApi.value) {
    setTimeout(() => {
      gridApi.value.sizeColumnsToFit();
    }, 100);
  }
};

// Define emits for parent components to listen to
// const emit = defineEmits(['return-to-project']); // Removed duplicate - now defined above

// Return to the project details page
const returnToProject = () => {
  // If this component is used inside a project details page, emit an event to parent
  // that will be handled by the ProjectDetails.vue's returnToProject method
  if (props.projectId) {
    emit('return-to-project');
    return;
  }

  // Otherwise navigate back to project (if coming from route)
  if (sourceProject.value && sourceProject.value.id) {
    router.push({
      path: '/project/' + sourceProject.value.id
    });
  } else {
    // Fallback to projects listing if something went wrong
    router.push('/projects');
  }
};

// Component lifecycle hooks
onMounted(async () => {
  // Load files when component mounts
  if (props.projectId) {
    await fetchFiles();
  } else {
    const errorMsg = t('notifications.selectFirmFirst');
    notifyError(errorMsg);
    error.value = errorMsg;
  }

  // Add resize event listener for the grid
  window.addEventListener('resize', onWindowResize);
});

onUnmounted(() => {
  // Remove resize event listener when component is unmounted
  window.removeEventListener('resize', onWindowResize);
  
  // Clean up annotator URL if it exists
  if (documentAnnotatorUrl.value) {
    URL.revokeObjectURL(documentAnnotatorUrl.value);
  }
});

// Watch for projectId changes
watch(() => props.projectId, async (newId: string | number | null) => {
  if (newId) {
    isInitialLoad.value = true;
    await fetchFiles();
  }
});
</script>

<style scoped>
:root {
  --primary: #6366f1;
  --primary-dark: #4f46e5;
  --primary-light: #e0e7ff;
  --primary-subtle: #f0f4ff;
  --success: #10b981;
  --success-light: #d1fae5;
  --danger: #f43f5e;
  --danger-light: #fce7f3;
  --warning: #f59e0b;
  --warning-light: #fef3c7;
  --info: #0ea5e9;
  --info-light: #e0f2fe;
  --neutral-50: #fafafa;
  --neutral-100: #f5f5f5;
  --neutral-200: #e5e5e5;
  --neutral-300: #d4d4d4;
  --neutral-400: #a3a3a3;
  --neutral-500: #737373;
  --neutral-600: #525252;
  --neutral-700: #404040;
  --neutral-800: #262626;
  --neutral-900: #171717;
  --space-1: 0.25rem;
  --space-2: 0.5rem;
  --space-3: 0.75rem;
  --space-4: 1rem;
  --space-6: 1.5rem;
  --space-8: 2rem;
  --space-12: 3rem;
  --radius-sm: 0.375rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
  --radius-2xl: 1.5rem;
  --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  --shadow-md: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
  --shadow-lg: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
  --shadow-xl: 0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1);
}

.file-manager-wrapper {
  width: 100%;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  min-height: 100vh;
  padding: var(--space-6);
}

.project-documents-section {
  max-width: 1400px;
  margin: 0 auto;
}

/* Project context styling */
.project-context {
  background: white;
  border-radius: var(--radius-2xl);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--neutral-200);
}

.project-context-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.project-info h3 {
  margin: 0;
  color: var(--neutral-900);
  font-size: 1.25rem;
  font-weight: 700;
}

.project-info p {
  margin: var(--space-2) 0 0;
  color: var(--neutral-600);
}

.return-to-project-btn {
  white-space: nowrap;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  border: none;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  font-weight: 600;
  transition: all 0.3s ease;
}

.return-to-project-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
}

.return-to-project-btn .btn-icon {
  margin-right: var(--space-2);
}

/* Document Statistics */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-4);
  margin-bottom: var(--space-8);
}

.stat-card {
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--neutral-200);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: var(--primary);
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
}

.stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--space-4);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
}

.stat-icon.files {
  background: var(--primary-light);
  color: var(--primary);
}

.stat-icon.storage {
  background: var(--success-light);
  color: var(--success);
}

.stat-icon.recent {
  background: var(--warning-light);
  color: var(--warning);
}

.stat-number {
  font-size: 2rem;
  font-weight: 800;
  color: var(--neutral-900);
  margin-bottom: var(--space-1);
}

.stat-label {
  color: var(--neutral-600);
  font-size: 0.875rem;
  font-weight: 500;
}

/* Search and Filter Controls */
.controls-section {
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--neutral-200);
}

.controls-header {
  margin-bottom: var(--space-6);
}

.controls-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--neutral-900);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  margin: 0;
}

.filter-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.filter-left {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-3);
  align-items: center;
  flex: 1;
  min-width: 0;
}

.filter-right {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  flex-shrink: 0;
}

.filter-label {
  font-weight: 600;
  color: var(--neutral-700);
  font-size: 0.875rem;
}

.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.filter-chip {
  background: var(--neutral-100);
  border: 2px solid var(--neutral-200);
  color: var(--neutral-700);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-lg);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.filter-chip:hover {
  border-color: var(--neutral-300);
  background: var(--neutral-50);
}

.filter-chip.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
}

.chip-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 0.75rem;
  min-width: 20px;
  text-align: center;
}

.filter-chip.active .chip-count {
  background: rgba(255, 255, 255, 0.3);
}

.filter-chip:not(.active) .chip-count {
  background: var(--neutral-200);
  color: var(--neutral-600);
}

/* Documents Section */
.documents-section {
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--neutral-200);
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: var(--space-6);
}

.document-card {
  background: white;
  border: 2px solid var(--neutral-100);
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: hidden;
  animation: fadeInUp 0.6s ease;
}

.document-card::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(79, 70, 229, 0.05) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.document-card:hover::before {
  opacity: 1;
}

.document-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-xl);
  border-color: var(--primary-light);
}

.document-header {
  display: flex;
  align-items: flex-start;
  gap: var(--space-4);
  margin-bottom: var(--space-4);
  position: relative;
  z-index: 1;
}

.document-icon {
  width: 48px;
  height: 48px;
  border-radius: var(--radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.document-icon.pdf {
  background: linear-gradient(135deg, #ff4757, #ff6b7a);
  color: white;
}

.document-icon.image {
  background: linear-gradient(135deg, #3742fa, #5352ed);
  color: white;
}

.document-icon.word {
  background: linear-gradient(135deg, #2f54eb, #1890ff);
  color: white;
}

.document-icon.excel {
  background: linear-gradient(135deg, #52c41a, #73d13d);
  color: white;
}

.document-icon.powerpoint {
  background: linear-gradient(135deg, #fa8c16, #ffa940);
  color: white;
}

.document-icon.archive {
  background: linear-gradient(135deg, #722ed1, #9254de);
  color: white;
}

.document-icon.text {
  background: linear-gradient(135deg, #8c8c8c, #bfbfbf);
  color: white;
}

.document-icon.default {
  background: linear-gradient(135deg, var(--neutral-400), var(--neutral-500));
  color: white;
}

.document-info {
  flex: 1;
  min-width: 0;
}

.document-name {
  font-weight: 700;
  font-size: 1rem;
  color: var(--neutral-900);
  margin: 0 0 var(--space-1) 0;
  word-break: break-word;
  line-height: 1.3;
}

.document-type-badge {
  display: inline-flex;
  align-items: center;
  padding: var(--space-1) var(--space-3);
  background: var(--neutral-100);
  color: var(--neutral-700);
  border-radius: var(--radius-md);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.document-meta {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-bottom: var(--space-4);
  position: relative;
  z-index: 1;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  font-size: 0.875rem;
  color: var(--neutral-600);
}

.meta-icon {
  width: 16px;
  color: var(--neutral-400);
}

.file-size {
  font-weight: 600;
  color: var(--neutral-700);
}

.file-date {
  color: var(--neutral-500);
}

.document-actions {
  display: flex;
  gap: var(--space-2);
  position: relative;
  z-index: 1;
}

.action-button {
  background: var(--neutral-100);
  border: 1px solid var(--neutral-200);
  color: var(--neutral-600);
  padding: var(--space-2);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
}

.action-button:hover {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  transform: scale(1.1);
}

.action-button.delete:hover {
  background: var(--danger);
  border-color: var(--danger);
}

.action-button.annotate:hover {
  background: var(--success);
  border-color: var(--success);
}

/* Enhanced loading, error, and empty state styles */
.loading-indicator,
.error-message,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: var(--space-12) var(--space-6);
  min-height: 400px;
}

.loading-content,
.error-content,
.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--space-4);
}

/* Loading state */
.loading-indicator {
  color: var(--neutral-600);
}

.spinner {
  width: 48px;
  height: 48px;
  border: 3px solid var(--neutral-200);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

/* Error state */
.error-message {
  color: var(--danger);
}

.error-icon {
  font-size: 3rem;
  color: var(--danger);
  margin-bottom: var(--space-2);
}

.error-content p {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 500;
}

.btn-outline {
  margin-top: var(--space-4);
  padding: var(--space-2) var(--space-4);
  border: 2px solid var(--primary);
  background: transparent;
  color: var(--primary);
  border-radius: var(--radius-md);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--primary);
    color: white;
  }
}

/* Empty state */
.empty-state {
  color: var(--neutral-500);
}

.empty-icon {
  font-size: 4rem;
  color: var(--neutral-300);
  margin-bottom: var(--space-4);
  
  i {
    opacity: 0.7;
  }
}

.empty-content h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--neutral-700);
}

.empty-content p {
  margin: 0;
  font-size: 1rem;
  color: var(--neutral-500);
  max-width: 300px;
  line-height: 1.5;
}

.empty-content .btn {
  margin-top: var(--space-4);
  padding: var(--space-3) var(--space-6);
  
  i {
    margin-right: var(--space-2);
  }
}

.error-message p {
  margin-bottom: var(--space-4);
}

.embedded-header {
  background: white;
  border-radius: var(--radius-xl);
  padding: var(--space-6);
  margin-bottom: var(--space-6);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--neutral-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.component-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--neutral-900);
  margin: 0;
}

.empty-state {
  text-align: center;
  color: var(--neutral-600);
}

.empty-state i {
  font-size: 4rem;
  color: var(--neutral-300);
  margin-bottom: var(--space-4);
}

.empty-state p {
  font-size: 1.125rem;
  margin-bottom: var(--space-6);
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
}

/* Upload button modern styling */
.upload-btn-wrapper {
  position: relative;
  overflow: hidden;
  display: inline-block;
}

.upload-file-btn,
.btn.btn-primary {
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
  color: white;
  border: none;
  padding: var(--space-4) var(--space-6);
  border-radius: var(--radius-xl);
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 15px rgba(99, 102, 241, 0.3);
  display: flex;
  align-items: center;
  gap: var(--space-2);
  position: relative;
  overflow: hidden;
}

.upload-file-btn::before,
.btn.btn-primary::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%);
  transform: translateX(-100%);
  transition: transform 0.6s;
}

.upload-file-btn:hover::before,
.btn.btn-primary:hover::before {
  transform: translateX(100%);
}

.upload-file-btn:hover,
.btn.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(99, 102, 241, 0.4);
}

.btn-icon {
  font-size: 1rem;
  display: flex;
  align-items: center;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Staggered animation for document cards */
.document-card:nth-child(1) { animation-delay: 0.1s; }
.document-card:nth-child(2) { animation-delay: 0.2s; }
.document-card:nth-child(3) { animation-delay: 0.3s; }
.document-card:nth-child(4) { animation-delay: 0.4s; }
.document-card:nth-child(5) { animation-delay: 0.5s; }
.document-card:nth-child(6) { animation-delay: 0.6s; }

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Accessibility improvements */
.document-card:focus {
  outline: 3px solid rgba(99, 102, 241, 0.3);
  outline-offset: 2px;
}

.action-button:focus {
  outline: 2px solid rgba(99, 102, 241, 0.5);
  outline-offset: 2px;
}

.upload-file-btn:focus,
.btn.btn-primary:focus {
  outline: 3px solid rgba(99, 102, 241, 0.3);
  outline-offset: 2px;
}

/* Pagination Controls */
.pagination-section {
  margin-top: var(--space-8);
  padding-top: var(--space-6);
  border-top: 1px solid var(--neutral-200);
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
}

.pagination-btn {
  background: white;
  border: 2px solid var(--neutral-200);
  color: var(--neutral-600);
  padding: var(--space-3);
  border-radius: var(--radius-lg);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  font-size: 1rem;
}

.pagination-btn:hover:not(.disabled) {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.pagination-btn.disabled {
  opacity: 0.4;
  cursor: not-allowed;
  background: var(--neutral-50);
  color: var(--neutral-300);
}

.pagination-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  margin: 0 var(--space-4);
}

.page-numbers {
  font-weight: 700;
  color: var(--neutral-900);
  font-size: 1rem;
}

.items-info {
  font-size: 0.875rem;
  color: var(--neutral-500);
  white-space: nowrap;
}

/* Page Size Selector */
.page-size-selector {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.page-size-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--neutral-700);
  white-space: nowrap;
}

.page-size-dropdown {
  background: white;
  border: 2px solid var(--neutral-200);
  color: var(--neutral-700);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-lg);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 60px;
}

.page-size-dropdown:hover {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.page-size-dropdown:focus {
  outline: none;
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.2);
}

/* Responsive Design */
@media (max-width: 1024px) {
  .file-manager-wrapper {
    padding: var(--space-4);
  }

  .documents-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: var(--space-4);
  }

  .stats-section {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--space-3);
  }
}

@media (max-width: 640px) {
  .file-manager-wrapper {
    padding: var(--space-3);
  }

  .documents-grid {
    grid-template-columns: 1fr;
  }

  .stats-section {
    grid-template-columns: repeat(2, 1fr);
  }

  .filter-chips {
    justify-content: center;
  }

  .project-context-content {
    flex-direction: column;
    gap: var(--space-4);
    align-items: stretch;
  }

  .embedded-header {
    flex-direction: column;
    gap: var(--space-4);
    align-items: stretch;
  }

  .pagination-controls {
    gap: var(--space-2);
  }

  .pagination-btn {
    width: 40px;
    height: 40px;
  }

  .pagination-info {
    margin: 0 var(--space-2);
    order: -1;
    margin-bottom: var(--space-4);
  }

  .filter-section {
    flex-direction: column;
    align-items: stretch;
    gap: var(--space-3);
  }
  
  .filter-left,
  .filter-right {
    flex-direction: column;
    align-items: flex-start;
    gap: var(--space-2);
  }

  .page-size-selector {
    flex-basis: 100%;
    margin-top: var(--space-2);
  }

  .page-size-label {
    font-size: 0.75rem;
  }

  .page-size-dropdown {
    font-size: 0.75rem;
    padding: var(--space-1) var(--space-2);
    min-width: 50px;
  }
}
</style>
